import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Creatable from "react-select/creatable";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";
import { Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ReactJson from "react-json-view";
import cn from "classnames";
import Layout from "../layout/Layout";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import socialApiClient from "../../cores/helpers/services/social-api-client";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import SyncAccount from "../component/SyncAccount/SyncAccount";
import { SocialProviderId } from "../../cores/socialPost/config";
import { isTiktokPostingEnabled } from "../../cores/config/featureFlags";
import { PostImage } from "../../cores/socialPost/interfaces/postImage";
import { getBase64 } from "../../cores/utils";
import { instagramApiService } from "../../cores/instagram/services/api";
import { facebookApiService } from "../../cores/facebook/services/api";
import { linkedInApiService } from "../../cores/linkedIn/services/api";
import { twitterApiService } from "../../cores/twitter/services/api";
import { tiktokApiService } from "../../cores/tiktok/services/api";
import { Errors } from "../../constants/errors";
import "../styles/SocialAutomatedPosts.scss";

type Option = { value: string; label: string };

type UploadedImage = { file: File; previewUrl: string };

type StepId = "config" | "media" | "preview";

// Proyecto = serie de publicaciones (se guarda para cerrar y retomar después).
type AutoPostProject = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
};

// Borrador persistido por proyecto (config + paso actual; medios se re-suben al volver).
type AutoPostDraft = {
  step: StepId;
  industry: string;
  topics: Option[];
  selectedDays: string[];
  startDateIso: string | null;
};

const STORAGE_KEY_PROJECTS = (companyId: string) =>
  `wembii_socialAuto_projects_${companyId || "default"}`;
const STORAGE_KEY_DRAFT = (companyId: string, projectId: string) =>
  `wembii_socialAuto_draft_${companyId || "default"}_${projectId}`;

function loadProjects(companyId: string | null): AutoPostProject[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROJECTS(companyId || ""));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AutoPostProject[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveProjects(companyId: string | null, list: AutoPostProject[]) {
  try {
    localStorage.setItem(
      STORAGE_KEY_PROJECTS(companyId || ""),
      JSON.stringify(list)
    );
  } catch {
    // ignore quota / private
  }
}

function loadDraft(
  companyId: string | null,
  projectId: string
): AutoPostDraft | null {
  try {
    const raw = localStorage.getItem(
      STORAGE_KEY_DRAFT(companyId || "", projectId)
    );
    if (!raw) return null;
    return JSON.parse(raw) as AutoPostDraft;
  } catch {
    return null;
  }
}

function saveDraft(
  companyId: string | null,
  projectId: string,
  draft: AutoPostDraft
) {
  try {
    localStorage.setItem(
      STORAGE_KEY_DRAFT(companyId || "", projectId),
      JSON.stringify(draft)
    );
  } catch {
    // ignore
  }
}

type PreviewPost = {
  id: string;
  date: Date;
  time: string;
  title: string;
  text: string;
  image: string;
  confidence: number;
  contentImageIndex: number;
};

type SocialPreviewId = "instagram" | "facebook" | "linkedin" | "twitter" | "tiktok";

const SOCIAL_PREVIEWS: Array<{ id: SocialPreviewId; label: string }> = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "X (Twitter)" },
  { id: "tiktok", label: "TikTok" },
];

/** Nombre de red que espera el endpoint openai/posts (como en el planificador principal). */
const OPENAI_SOCIAL_NAME: Record<SocialPreviewId, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "Twitter",
  tiktok: "TikTok",
};

type AutoPostSocialKey = "instagram" | "facebook" | "linkedIn" | "twitter" | "tiktok";

const AUTO_POST_REQUIRED_SOCIALS: Array<{
  previewId: SocialPreviewId;
  providerId: SocialProviderId;
  settingsKey: AutoPostSocialKey;
}> = [
  { previewId: "instagram", providerId: SocialProviderId.Instagram, settingsKey: "instagram" },
  { previewId: "facebook", providerId: SocialProviderId.Facebook, settingsKey: "facebook" },
  { previewId: "linkedin", providerId: SocialProviderId.LinkedIn, settingsKey: "linkedIn" },
  { previewId: "twitter", providerId: SocialProviderId.Twitter, settingsKey: "twitter" },
  { previewId: "tiktok", providerId: SocialProviderId.TikTok, settingsKey: "tiktok" },
];

const getAutoPostRequiredSocials = () =>
  AUTO_POST_REQUIRED_SOCIALS.filter(
    (s) => isTiktokPostingEnabled() || s.providerId !== SocialProviderId.TikTok
  );

const GENERATE_OR_EDIT_ENDPOINT = "nanobanana/generate-or-edit";
const STATUS_ENDPOINT = "nanobanana/status";
const DOWNLOAD_IMAGE_ENDPOINT = "nanobanana/download-image";

const INDUSTRIES: Option[] = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "salud", label: "Salud" },
  { value: "educacion", label: "Educación" },
  { value: "bienes-raices", label: "Bienes raíces" },
  { value: "servicios", label: "Servicios" },
  { value: "tecnologia", label: "Tecnología" },
  { value: "otros", label: "Otros" },
];

// Ideas de etiquetas para arrancar el MVP (luego se reemplaza por catálogo real).
const TOPIC_TAGS: Option[] = [
  { value: "promociones", label: "Promociones" },
  { value: "lanzamientos", label: "Lanzamientos" },
  { value: "tips", label: "Tips" },
  { value: "beneficios", label: "Beneficios" },
  { value: "casos", label: "Casos / Testimonios" },
  { value: "tutoriales", label: "Tutoriales" },
  { value: "eventos", label: "Eventos" },
  { value: "novedades", label: "Novedades" },
  { value: "cocina", label: "Lifestyle / Cocina" },
  { value: "moda", label: "Moda" },
  { value: "finanzas", label: "Finanzas" },
  { value: "bienestar", label: "Bienestar" },
];

const DAYS = [
  { key: "0", label: "Dom" },
  { key: "1", label: "Lun" },
  { key: "2", label: "Mar" },
  { key: "3", label: "Mié" },
  { key: "4", label: "Jue" },
  { key: "5", label: "Vie" },
  { key: "6", label: "Sáb" },
] as const;

const MAX_POSTS_PER_MONTH = 8;

function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  return `${dd}/${mm}/${yyyy}`;
}

function getTodayDate(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function buildScheduleDate(date: Date, time: string): string {
  const [hours, minutes] = time.split(":").map((v) => Number(v));
  const scheduled = new Date(date);
  scheduled.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 0, 0);
  return scheduled.toString();
}

function mulberry32(seed: number) {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function isPlaceholderCaption(text: string): boolean {
  return text.trim().startsWith("Se generará con ChatGPT");
}

/** Mismo criterio temático que el brief de NanoBanana; el caption refuerza la pieza visual. */
function buildChatGptCaptionAdditionalInfo(
  industryLabel: string,
  themeLabel: string
): string {
  return [
    `Contexto: el post visual ya fue o será generado con IA para industria "${industryLabel}" y tema "${themeLabel}".`,
    `Ese gráfico sigue la idea: gancho + beneficio + CTA (texto breve sobre la imagen, español natural, máx. 2 líneas en la pieza visual).`,
    `Tu tarea: escribir SOLO el pie de publicación (caption bajo la imagen) para la red social indicada.`,
    `Reglas del caption: español natural; máximo 2 líneas cortas; línea 1 = gancho o beneficio alineado al tema; línea 2 = CTA claro; coherente con el mismo mensaje que tendría la pieza visual (no contradecir el tema); sin hashtags ni emojis a menos que pidan.`,
  ].join(" ");
}

export default function SocialAutomatedPosts() {
  const intl = useIntl();
  const { companyId, socialSettings, getMeSocialSettings } = useMeCore();
  const { goToSocialCalendar } = useRouterCore();

  // Proyectos: listado y el que está abierto (null = pantalla listado).
  const [projects, setProjects] = useState<AutoPostProject[]>(() =>
    loadProjects(companyId)
  );
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentProjectName, setCurrentProjectName] = useState<string>("");

  // Sincronizar lista de proyectos cuando cambia companyId.
  React.useEffect(() => {
    const list = loadProjects(companyId);
    setProjects(list);
    setCurrentProjectId((prev) => {
      if (!prev) return null;
      return list.some((p) => p.id === prev) ? prev : null;
    });
  }, [companyId]);

  // Step control (dentro del wizard de un proyecto).
  const [step, setStep] = useState<StepId>("config");

  // Config step
  const [industry, setIndustry] = useState<string>("");
  const [topics, setTopics] = useState<Option[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(getTodayDate);

  // Media step
  const [logo, setLogo] = useState<UploadedImage | null>(null);
  const MAX_CONTENT_IMAGES = 5;
  const MIN_CONTENT_IMAGES = 3;
  const [images, setImages] = useState<(UploadedImage | null)[]>(
    Array.from({ length: 5 }).map(() => null)
  );

  const [replaceSlotIndex, setReplaceSlotIndex] = useState<number | null>(
    null
  );
  const [isDraggingImages, setIsDraggingImages] = useState(false);

  // Preview step
  const [previewSeed, setPreviewSeed] = useState<number>(() => Date.now());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [activePreviewPost, setActivePreviewPost] = useState<
    PreviewPost | null
  >(null);
  const [activeSocialPreview, setActiveSocialPreview] =
    useState<SocialPreviewId>("instagram");

  // NanoBanana image edits, per-post (so each day can reach its "ideal" image).
  const [postImageOverrides, setPostImageOverrides] = useState<
    Record<string, UploadedImage>
  >({});
  const [postTextOverrides, setPostTextOverrides] = useState<
    Record<string, string>
  >({});
  const [postScheduleOverrides, setPostScheduleOverrides] = useState<
    Record<string, { date: Date; time: string }>
  >({});
  const [postTextDraft, setPostTextDraft] = useState("");
  const [nanoEditPrompt, setNanoEditPrompt] = useState("");
  const [isEditingNanoBanana, setIsEditingNanoBanana] = useState(false);
  const [isGeneratingPostText, setIsGeneratingPostText] = useState(false);
  const [nanoEditProgress, setNanoEditProgress] = useState(0);
  const [isGeneratingNanoPosts, setIsGeneratingNanoPosts] = useState(false);
  const [nanoPostsProgress, setNanoPostsProgress] = useState<{
    done: number;
    total: number;
  }>({ done: 0, total: 0 });
  const modalImageInputRef = useRef<HTMLInputElement>(null);

  const [approvalMode, setApprovalMode] = useState(false);
  const [approvedPostIds, setApprovedPostIds] = useState<Record<string, boolean>>(
    {}
  );

  const [syncAccountsModalOpen, setSyncAccountsModalOpen] = useState(false);
  const [smartScheduleOpen, setSmartScheduleOpen] = useState(false);
  const [selectedSmartTime, setSelectedSmartTime] = useState<string | null>(null);

  // Horarios sugeridos por engagement (mock para sugerencia en Config).
  const smartScheduleSuggestions = useMemo(
    () => [
      { time: "15:00", label: "03:00 PM", engagement: "+32%", stars: 5 },
      { time: "09:00", label: "09:00 AM", engagement: "+27%", stars: 4 },
      { time: "09:30", label: "09:30 AM", engagement: "+27%", stars: 4 },
      { time: "10:00", label: "10:00 AM", engagement: "+27%", stars: 4 },
      { time: "10:30", label: "10:30 AM", engagement: "+27%", stars: 4 },
    ],
    []
  );

  // Crear proyecto: nombre temporal antes de crear.
  const [newProjectName, setNewProjectName] = useState("");

  const missingRequiredSocials = useMemo(() => {
    if (!socialSettings) {
      return getAutoPostRequiredSocials().map((s) => s.previewId);
    }

    const out: SocialPreviewId[] = [];
    getAutoPostRequiredSocials().forEach((s) => {
      const has = Boolean((socialSettings as any)?.[s.settingsKey]);
      if (!has) out.push(s.previewId);
    });
    return out;
  }, [socialSettings]);

  const hasAnySocialSynced = useMemo(
    () => missingRequiredSocials.length < getAutoPostRequiredSocials().length,
    [missingRequiredSocials.length]
  );

  /** Sin token de OpenAI en integraciones, el backend suele responder 403 a openai/posts. */
  const hasOpenAiConfigured = useMemo(
    () => Boolean((socialSettings as { openai?: { token?: string } })?.openai?.token?.length),
    [socialSettings]
  );

  // Solo redes sincronizadas para mostrar en el modal de detalle del post.
  const syncedSocialPreviews = useMemo(
    () => SOCIAL_PREVIEWS.filter((s) => !missingRequiredSocials.includes(s.id)),
    [missingRequiredSocials]
  );

  // Si el modal está abierto y la pestaña activa no está sincronizada, pasar a la primera sincronizada.
  useEffect(() => {
    if (
      !isPostModalOpen ||
      syncedSocialPreviews.length === 0 ||
      syncedSocialPreviews.some((s) => s.id === activeSocialPreview)
    ) {
      return;
    }
    setActiveSocialPreview(syncedSocialPreviews[0].id);
  }, [
    isPostModalOpen,
    syncedSocialPreviews,
    activeSocialPreview,
  ]);

  useEffect(() => {
    setPostTextDraft(activePreviewPost?.text ?? "");
  }, [activePreviewPost]);

  const refreshSocialSettings = useCallback(() => {
    if (companyId) getMeSocialSettings(companyId);
  }, [companyId, getMeSocialSettings]);

  const closePostModal = useCallback(() => {
    setIsPostModalOpen(false);
    setActivePreviewPost(null);
    setNanoEditPrompt("");
    setNanoEditProgress(0);
    setIsEditingNanoBanana(false);
  }, []);

  // Persistir borrador del proyecto actual (config + paso).
  const persistCurrentDraft = useCallback(() => {
    if (!currentProjectId || !companyId) return;
    const draft: AutoPostDraft = {
      step,
      industry,
      topics,
      selectedDays,
      startDateIso: startDate ? startDate.toISOString() : null,
    };
    saveDraft(companyId, currentProjectId, draft);
    setProjects((prev) => {
      const list = prev.map((p) =>
        p.id === currentProjectId
          ? { ...p, updatedAt: Date.now() }
          : p
      );
      saveProjects(companyId, list);
      return list;
    });
  }, [
    companyId,
    currentProjectId,
    step,
    industry,
    topics,
    selectedDays,
    startDate,
  ]);

  const createProject = useCallback(
    (name: string) => {
      const id = `proj_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const now = Date.now();
      const newProject: AutoPostProject = {
        id,
        name: name.trim() || intl.formatMessage({ id: "app.socialAutomatedPosts.project.unnamed", defaultMessage: "Sin nombre" }),
        createdAt: now,
        updatedAt: now,
      };
      setProjects((prev) => {
        const next = [...prev, newProject];
        saveProjects(companyId, next);
        return next;
      });
      setCurrentProjectId(id);
      setCurrentProjectName(newProject.name);
      setStep("config");
      setIndustry("");
      setTopics([]);
      setSelectedDays([]);
      setStartDate(getTodayDate());
      setLogo(null);
      setImages(Array.from({ length: 5 }).map(() => null));
      setPostImageOverrides({});
      setApprovalMode(false);
      setApprovedPostIds({});
    },
    [companyId, intl]
  );

  const openProject = useCallback(
    (project: AutoPostProject) => {
      setCurrentProjectId(project.id);
      setCurrentProjectName(project.name);
      const draft = loadDraft(companyId, project.id);
      if (draft) {
        setStep(draft.step);
        setIndustry(draft.industry);
        setTopics(draft.topics);
        setSelectedDays(draft.selectedDays);
        setStartDate(draft.startDateIso ? new Date(draft.startDateIso) : getTodayDate());
      } else {
        setStep("config");
        setIndustry("");
        setTopics([]);
        setSelectedDays([]);
        setStartDate(getTodayDate());
      }
      setLogo(null);
      setImages(Array.from({ length: 5 }).map(() => null));
      setPostImageOverrides({});
      setApprovalMode(false);
      setApprovedPostIds({});
    },
    [companyId]
  );

  const deleteProject = useCallback(
    (projectId: string, projectName: string) => {
      const message = intl.formatMessage(
        {
          id: "app.socialAutomatedPosts.projects.deleteConfirm",
          defaultMessage: '¿Eliminar el proyecto "{name}"? Se borrará el borrador guardado.',
        },
        { name: projectName }
      );
      if (!window.confirm(message)) return;
      setProjects((prev) => {
        const next = prev.filter((p) => p.id !== projectId);
        saveProjects(companyId, next);
        return next;
      });
      try {
        localStorage.removeItem(STORAGE_KEY_DRAFT(companyId || "", projectId));
      } catch {
        // ignore
      }
      if (currentProjectId === projectId) {
        setCurrentProjectId(null);
        setCurrentProjectName("");
      }
    },
    [companyId, intl, currentProjectId]
  );

  const backToProjectList = useCallback(() => {
    persistCurrentDraft();
    setCurrentProjectId(null);
    setCurrentProjectName("");
    setStep("config");
  }, [persistCurrentDraft]);

  // Guardar borrador al cambiar config o paso (debounced) para poder cerrar y retomar.
  React.useEffect(() => {
    if (!currentProjectId) return;
    const t = window.setTimeout(() => persistCurrentDraft(), 800);
    return () => clearTimeout(t);
  }, [currentProjectId, step, industry, topics, selectedDays, startDate, persistCurrentDraft]);

  const config = useMemo(() => {
    const industryLabel = INDUSTRIES.find((i) => i.value === industry)?.label;
    const topicLabels = topics.map((t) => t.label);
    const daysLabels = selectedDays
      .map((d) => DAYS.find((x) => x.key === d)?.label)
      .filter(Boolean);

    return {
      industry,
      industryLabel,
      topics: topics.map((t) => t.value),
      topicLabels,
      days: selectedDays.map((d) => Number(d)),
      daysLabels,
      startDate: startDate ? formatDate(startDate) : null,
      media: {
        logo: logo?.file.name ?? null,
        contentImages: images.filter(Boolean).map((i) => i!.file.name),
      },
    };
  }, [industry, images, logo, selectedDays, startDate, topics]);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (step === "config") {
      if (!industry) e.industry = "required";
      if (!topics.length) e.topics = "required";
      if (selectedDays.length < 1) e.days = "required";
      if (selectedDays.length > 2) e.days = "max";
      if (!startDate) e.startDate = "required";
    }
    if (step === "media") {
      if (!logo) e.logo = "required";
      const filled = images.filter(Boolean).length;
      if (filled < MIN_CONTENT_IMAGES) e.images = "requiredMin";
      if (filled > MAX_CONTENT_IMAGES) e.images = "requiredMax";
    }
    return e;
  }, [images, industry, logo, selectedDays, startDate, step, topics]);

  const canProceedToMedia = useMemo(() => {
    const filledTopics = topics.length > 0;
    const validDays = selectedDays.length >= 1 && selectedDays.length <= 2;
    return Boolean(industry && filledTopics && validDays && startDate);
  }, [industry, selectedDays, startDate, topics.length]);

  const canProceedToPreview = useMemo(() => {
    const filled = images.filter(Boolean).length;
    return Boolean(logo && filled >= MIN_CONTENT_IMAGES && filled <= MAX_CONTENT_IMAGES);
  }, [images, logo, MIN_CONTENT_IMAGES, MAX_CONTENT_IMAGES]);

  const toggleDay = useCallback(
    (dayKey: string) => {
      setSelectedDays((prev) => {
        const exists = prev.includes(dayKey);
        if (exists) return prev.filter((d) => d !== dayKey);
        if (prev.length >= 2) return prev; // max 2
        return [...prev, dayKey];
      });
    },
    [setSelectedDays]
  );

  const handleIndustryChange = useCallback(
    (value: string) => {
      setIndustry(value);
    },
    [setIndustry]
  );

  const onLogoSelected = useCallback((file: File | null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setLogo({ file, previewUrl: url });
  }, []);

  const pickImagesForSlots = useCallback(
    (files: FileList | null, replaceIndex?: number | null) => {
      if (!files || files.length === 0) return;

      const fileArr = Array.from(files).slice(0, MAX_CONTENT_IMAGES);
      const urls = fileArr.map((f) => URL.createObjectURL(f));
      const nextUploads: UploadedImage[] = fileArr.map((f, idx) => ({
        file: f,
        previewUrl: urls[idx],
      }));

      setImages((prev) => {
        const out = [...prev];
        if (typeof replaceIndex === "number") {
          out[replaceIndex] = nextUploads[0] ?? null;
          return out;
        }

        // fill empty slots, then ignore extras
        let uploadIdx = 0;
        for (
          let i = 0;
          i < out.length && uploadIdx < nextUploads.length;
          i++
        ) {
          if (!out[i]) {
            out[i] = nextUploads[uploadIdx];
            uploadIdx += 1;
          }
        }
        return out;
      });
    },
    [MAX_CONTENT_IMAGES]
  );

  const clearSlot = useCallback((index: number) => {
    setImages((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  }, []);

  const validateAndNext = useCallback(() => {
    if (!canProceedToMedia) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.validation.config",
          defaultMessage:
            "Completa la configuración: industria, temáticas, 1-2 días y fecha.",
        })
      );
      return;
    }
    setStep("media");
    persistCurrentDraft();
  }, [canProceedToMedia, intl, persistCurrentDraft]);

  const validateAndNextToPreview = useCallback(() => {
    if (!canProceedToPreview) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.validation.media",
          defaultMessage:
            `Sube el logo y entre ${MIN_CONTENT_IMAGES} y ${MAX_CONTENT_IMAGES} imágenes para poder previsualizar.`,
        })
      );
      return;
    }
    setStep("preview");
    persistCurrentDraft();
  }, [canProceedToPreview, intl, persistCurrentDraft]);

  const previewDates = useMemo(() => {
    if (!startDate || selectedDays.length === 0) return [];
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const year = start.getFullYear();
    const month = start.getMonth();
    const days = selectedDays.map((d) => Number(d));
    const out: Date[] = [];
    for (let i = 0; out.length < MAX_POSTS_PER_MONTH && i < 31; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      if (date.getMonth() !== month || date.getFullYear() !== year) break;
      if (days.includes(date.getDay())) out.push(date);
    }
    return out;
  }, [selectedDays, startDate]);

  const previewPosts = useMemo<PreviewPost[]>(() => {
    if (step !== "preview") return [];
    const rand = mulberry32(previewSeed);
    const industryLabel =
      INDUSTRIES.find((i) => i.value === industry)?.label ?? "Tu industria";
    const topicLabels = topics.map((t) => t.label);
    const filledImages = images
      .map((img, idx) => (img ? { idx, url: img.previewUrl } : null))
      .filter(Boolean) as Array<{ idx: number; url: string }>;

    return previewDates.map((date, idx) => {
      const id = `${idx}-${date.toISOString()}`;
      const scheduleOverride = postScheduleOverrides[id];
      const resolvedDate = scheduleOverride
        ? new Date(scheduleOverride.date)
        : new Date(date);
      const defaultTime = idx % 2 === 0 ? "09:00" : "18:00";
      const time = scheduleOverride?.time ?? selectedSmartTime ?? defaultTime;
      const topic = topicLabels.length ? topicLabels[idx % topicLabels.length] : "Tema";
      const title = `Promo ${topic} (${industryLabel})`;
      const defaultText = `Se generará con ChatGPT: ${topic} para ${industryLabel}. Ajustado para publicarse en ${formatDate(resolvedDate)} a las ${time}.`;
      const text = postTextOverrides[id] ?? defaultText;
      const imageIdx = filledImages.length ? filledImages[idx % filledImages.length].idx : 0;
      const defaultImage = filledImages.length
        ? filledImages[idx % filledImages.length]?.url ?? ""
        : "";

      const overrideImage = postImageOverrides[id]?.previewUrl;
      const image = overrideImage ?? defaultImage;
      return {
        id,
        date: resolvedDate,
        time,
        title,
        text,
        image,
        confidence: Math.round((0.74 + rand() * 0.22) * 100) / 100,
        contentImageIndex: imageIdx,
      };
    });
  }, [
    images,
    industry,
    previewDates,
    previewSeed,
    step,
    topics,
    postImageOverrides,
    postScheduleOverrides,
    postTextOverrides,
    selectedSmartTime,
  ]);

  /** Si ya hay copy de ChatGPT o manual guardado, no lo pisa al mover fecha/hora. */
  const resolveCaptionAfterScheduleChange = useCallback(
    (postId: string, newDate: Date, newTime: string) => {
      const override = postTextOverrides[postId];
      if (override !== undefined && override.trim() !== "") {
        return override;
      }
      const idx = previewPosts.findIndex((p) => p.id === postId);
      const industryLabel =
        INDUSTRIES.find((i) => i.value === industry)?.label ?? "Tu industria";
      const topicLabels = topics.map((t) => t.label);
      const topic =
        topicLabels.length > 0
          ? topicLabels[(idx >= 0 ? idx : 0) % topicLabels.length]
          : "Tema";
      return `Se generará con ChatGPT: ${topic} para ${industryLabel}. Ajustado para publicarse en ${formatDate(newDate)} a las ${newTime}.`;
    },
    [postTextOverrides, previewPosts, industry, topics]
  );

  // Tras generar imagen + texto en lote, el modal debe mostrar el copy real de ChatGPT.
  useEffect(() => {
    if (!isPostModalOpen || !activePreviewPost) return;
    const latest = previewPosts.find((p) => p.id === activePreviewPost.id);
    if (!latest) return;
    const draftMatchesDisplayed = postTextDraft === activePreviewPost.text;
    if (
      draftMatchesDisplayed &&
      (latest.text !== activePreviewPost.text ||
        latest.image !== activePreviewPost.image)
    ) {
      setActivePreviewPost(latest);
      setPostTextDraft(latest.text);
    }
  }, [
    previewPosts,
    isPostModalOpen,
    activePreviewPost?.id,
    activePreviewPost?.text,
    activePreviewPost?.image,
    postTextDraft,
  ]);

  const getNanoBananaImageSizeForSocial = useCallback(
    (socialId: SocialPreviewId) => {
      if (socialId === "tiktok") return "9:16";
      if (socialId === "instagram") return "4:5";
      return "16:9";
    },
    []
  );

  const pollNanobananaTaskStatus = useCallback(
    async (taskId: string) => {
      const maxAttempts = 30;
      const delayMs = 2000;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        setNanoEditProgress(70 + Math.round((attempt / maxAttempts) * 30));

        const { data } = await socialApiClient().get(STATUS_ENDPOINT, {
          params: { taskId, companyId },
        });

        const taskPayload = data?.payload || data;
        const resultUrl =
          taskPayload?.resultImageUrl ||
          taskPayload?.raw?.response?.resultImageUrl ||
          taskPayload?.raw?.data?.response?.resultImageUrl;

        if (taskPayload?.status === "success" && resultUrl) {
          return resultUrl as string;
        }

        if (taskPayload?.raw?.errorMessage) {
          throw new Error(taskPayload.raw.errorMessage as string);
        }

        if (attempt < maxAttempts) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      throw new Error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errTimeout",
          defaultMessage: "Tiempo de espera agotado editando la imagen.",
        })
      );
    },
    [companyId, intl]
  );

  const buildNanoPostPrompt = useCallback(
    (industryLabel: string, themeLabel: string) => `
You are a graphic designer and direct-response copywriter.

Create a FINAL social media post (not a mockup) using:
- Industry: ${industryLabel}
- Theme: ${themeLabel}
- Attached logo
- Attached image

Rules:
- Use the image as background.
- Use ONLY colors from the logo.
- Integrate the logo cleanly.
- Match style to the industry and theme.

IMPORTANT:
Write in clear, natural SPANISH (no errors, no awkward phrasing).

Text rules (VERY IMPORTANT):
- Maximum 8 words per line
- Maximum 2 lines total
- Simple, direct, and persuasive
- Focus on ONE idea related to the theme

Structure:
- Line 1: Hook / main benefit
- Line 2: CTA

Place the text directly on the image.
Do NOT explain anything.

Output ONLY the final text.
`.trim(),
    []
  );

  const mapOpenAiPostError = useCallback(
    (error: any): string => {
      const status =
        error?.response?.status ??
        (typeof error?.request?.status === "number"
          ? error.request.status
          : undefined);
      let payload: { code?: number; message?: string } = {};
      if (error?.response?.data && typeof error.response.data === "object") {
        payload = error.response.data;
      } else if (
        error?.request?.response &&
        typeof error.request.response === "string"
      ) {
        try {
          payload = JSON.parse(error.request.response);
        } catch {
          // ignore
        }
      }
      if (status === 403) {
        const code = payload.code ?? 0;
        if (code === Errors.InsufficientQuota) {
          return intl.formatMessage({ id: "app.social.openai.quota" });
        }
        if (code === Errors.InvalidApiKey) {
          return intl.formatMessage({ id: "app.social.openai.invalidapikey" });
        }
        return intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.forbidden",
          defaultMessage:
            "ChatGPT no está disponible (403). Revisa la API key de OpenAI en Integraciones o el saldo de tu cuenta.",
        });
      }
      if (status === 401) {
        return intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.unauthorized",
          defaultMessage: "Sesión expirada. Vuelve a iniciar sesión.",
        });
      }
      const msg =
        (typeof payload.message === "string" && payload.message) ||
        error?.message ||
        intl.formatMessage({ id: "app.toast.error82" });
      return msg;
    },
    [intl]
  );

  const generatePostTextWithChatGpt = useCallback(
    async (
      args: { industryLabel: string; themeLabel: string; socialMedia: string },
      companyIdParam: string
    ) => {
      const { industryLabel, themeLabel, socialMedia } = args;
      const body = {
        industry: industryLabel,
        subject: `${themeLabel} · ${industryLabel}`,
        emoticons: false,
        hashtags: false,
        additionalInfo: buildChatGptCaptionAdditionalInfo(industryLabel, themeLabel),
        socialMedias: [socialMedia],
      };
      let data: any;
      try {
        const res = await socialApiClient().post("openai/posts", body, {
          params: { companyId: companyIdParam },
        });
        data = res.data;
      } catch (error: any) {
        throw new Error(mapOpenAiPostError(error));
      }
      const payload = data?.payload ?? data;
      const templates = Array.isArray(payload?.templates)
        ? payload.templates
        : payload?.template
          ? [payload.template]
          : Array.isArray(data?.templates)
            ? data.templates
            : [];
      const first = templates[0] as { body?: string; text?: string } | undefined;
      const content =
        first?.body ||
        first?.text ||
        (typeof payload?.body === "string" ? payload.body : "") ||
        (typeof data?.body === "string" ? data.body : "") ||
        "";
      const out = typeof content === "string" ? content.trim() : "";
      if (!out) {
        throw new Error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.openai.emptyResponse",
            defaultMessage: "ChatGPT no devolvió texto para el post.",
          })
        );
      }
      return out;
    },
    [intl, mapOpenAiPostError]
  );

  // Al abrir el post, si aún no hay copy real, generarlo con ChatGPT (sin depender del botón de imágenes).
  useEffect(() => {
    if (!isPostModalOpen || !activePreviewPost || !companyId) return;
    if (!hasOpenAiConfigured) return;
    if (!isPlaceholderCaption(activePreviewPost.text)) return;

    let cancelled = false;
    const postId = activePreviewPost.id;

    (async () => {
      setIsGeneratingPostText(true);
      try {
        const postIndex = previewPosts.findIndex((p) => p.id === postId);
        const industryLabel =
          INDUSTRIES.find((i) => i.value === industry)?.label ?? "Tu industria";
        const themeLabel =
          topics.length > 0
            ? topics[(postIndex >= 0 ? postIndex : 0) % topics.length].label
            : "General";
        const socialMedia =
          OPENAI_SOCIAL_NAME[activeSocialPreview] ?? "Instagram";
        const generatedText = await generatePostTextWithChatGpt(
          { industryLabel, themeLabel, socialMedia },
          companyId
        );
        if (cancelled) return;
        setPostTextOverrides((prev) => ({
          ...prev,
          [postId]: generatedText,
        }));
        setPostTextDraft(generatedText);
        setActivePreviewPost((prev) =>
          prev && prev.id === postId ? { ...prev, text: generatedText } : prev
        );
      } catch (e: any) {
        if (!cancelled) {
          toast.error(
            e?.message ||
              intl.formatMessage({
                id: "app.socialAutomatedPosts.modal.editText.autoGenErr",
                defaultMessage:
                  'No se pudo generar el texto automáticamente. Prueba "Regenerar texto con ChatGPT".',
              })
          );
        }
      } finally {
        if (!cancelled) setIsGeneratingPostText(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isPostModalOpen,
    activePreviewPost?.id,
    activePreviewPost?.text,
    companyId,
    previewPosts,
    industry,
    topics,
    activeSocialPreview,
    generatePostTextWithChatGpt,
    intl,
    hasOpenAiConfigured,
  ]);

  const handleEditActivePostImage = useCallback(async () => {
    if (!activePreviewPost) return;
    if (!companyId) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoCompany",
          defaultMessage: "No hay companyId disponible para NanoBanana.",
        })
      );
      return;
    }

    const prompt = nanoEditPrompt.trim();
    if (!prompt) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errPrompt",
          defaultMessage: "Indica qué quieres mejorar en la imagen.",
        })
      );
      return;
    }

    const postId = activePreviewPost.id;
    const override = postImageOverrides[postId];
    const slotUpload = images[activePreviewPost.contentImageIndex];
    const baseUpload = override ?? slotUpload ?? null;

    if (!baseUpload?.file) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoBaseImage",
          defaultMessage: "No hay una imagen base para editar en este post.",
        })
      );
      return;
    }

    setIsEditingNanoBanana(true);
    setNanoEditProgress(10);
    try {
      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("ecoMode", "false");
      formData.append("images", baseUpload.file);

      // Edit mode (imagen -> imagen)
      formData.append("type", "IMAGETOIAMGE");
      formData.append("numImages", "1");
      formData.append("imageSize", getNanoBananaImageSizeForSocial(activeSocialPreview));

      const { data } = await socialApiClient().post(
        GENERATE_OR_EDIT_ENDPOINT,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { companyId },
        }
      );

      const providerCode = data?.payload?.raw?.code;
      const providerMessage = (data?.payload?.raw?.msg || "").toString();
      const insufficientCredits =
        providerCode === 402 ||
        providerMessage.toLowerCase().includes("insufficient");

      if (insufficientCredits) {
        throw new Error(
          intl.formatMessage({
            id: "app.aiGeneration.errInsufficientCredits",
            defaultMessage:
              "No tienes saldo suficiente en NanoBanana para generar la imagen. Recarga creditos e intentalo nuevamente.",
          })
        );
      }

      const taskId = data?.payload?.taskId || data?.data?.taskId || data?.taskId;
      if (!taskId) {
        throw new Error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.nanobanana.errNoTaskId",
            defaultMessage: "No se pudo iniciar la edición en NanoBanana.",
          })
        );
      }

      const resultImageUrl = await pollNanobananaTaskStatus(String(taskId));
      setNanoEditProgress(100);

      const { data: blob, headers } = await socialApiClient().get(
        DOWNLOAD_IMAGE_ENDPOINT,
        {
          responseType: "blob",
          params: { imageUrl: resultImageUrl, companyId },
        }
      );

      const contentType =
        headers["content-type"] || blob.type || "image/jpeg";
      const ext = contentType.includes("png")
        ? "png"
        : contentType.includes("webp")
          ? "webp"
          : "jpg";

      const file = new File(
        [blob],
        `nanobanana-edit-${Date.now()}.${ext}`,
        { type: contentType }
      );
      const previewUrl = URL.createObjectURL(file);

      setPostImageOverrides((prev) => {
        const prevItem = prev[postId];
        if (prevItem?.previewUrl) URL.revokeObjectURL(prevItem.previewUrl);
        return {
          ...prev,
          [postId]: { file, previewUrl },
        };
      });

      setActivePreviewPost((prev) => {
        if (!prev) return prev;
        return { ...prev, image: previewUrl };
      });

      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.editImage.editedOk",
          defaultMessage: "Imagen actualizada correctamente",
        })
      );
      setNanoEditPrompt("");
    } catch (e: any) {
      toast.error(
        e?.message ||
          intl.formatMessage({
            id: "app.socialAutomatedPosts.nanobanana.editedErr",
            defaultMessage: "Ocurrió un error editando la imagen.",
          })
      );
    } finally {
      setIsEditingNanoBanana(false);
    }
  }, [
    activePreviewPost,
    activeSocialPreview,
    companyId,
    getNanoBananaImageSizeForSocial,
    intl,
    images,
    nanoEditPrompt,
    pollNanobananaTaskStatus,
    postImageOverrides,
  ]);

  const handleChangePostImageByFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!activePreviewPost) return;
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      const previewUrl = URL.createObjectURL(file);
      setPostImageOverrides((prev) => {
        const prevItem = prev[activePreviewPost.id];
        if (prevItem?.previewUrl) URL.revokeObjectURL(prevItem.previewUrl);
        return {
          ...prev,
          [activePreviewPost.id]: { file, previewUrl },
        };
      });
      setActivePreviewPost((prev) =>
        prev ? { ...prev, image: previewUrl } : prev
      );
      e.target.value = "";
      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.changeImage.uploaded",
          defaultMessage: "Imagen reemplazada correctamente",
        })
      );
    },
    [activePreviewPost, intl]
  );

  const monthBase = useMemo(() => {
    // Mock: si el usuario aún no eligió fecha, mostramos abril 2026.
    const base = previewDates?.[0]
      ? new Date(previewDates[0])
      : startDate
        ? new Date(startDate)
        : new Date(2026, 3, 1);
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [previewDates, startDate]);

  const calendarModel = useMemo(() => {
    const monthStart = new Date(monthBase.getFullYear(), monthBase.getMonth(), 1);
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const totalDays = monthEnd.getDate();
    const firstDayIndex = monthStart.getDay(); // 0..6 (Dom..Sáb)

    const daysByKey = new Map<string, typeof previewPosts[number]>();
    previewPosts.forEach((p) => {
      const key = formatDate(p.date);
      daysByKey.set(key, p);
    });

    const locale = intl.locale || "es";
    const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(
      monthStart
    );
    const title = `${monthName} ${monthStart.getFullYear()}`;

    const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    const cells: Array<{ key: string | null; dayNumber: number | null; date: Date | null }> =
      [];

    for (let i = 0; i < firstDayIndex; i++) {
      cells.push({ key: null, dayNumber: null, date: null });
    }
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(monthStart);
      date.setDate(day);
      const key = formatDate(date);
      cells.push({
        key,
        dayNumber: day,
        date,
      });
    }

    return { title, weekDays, cells, daysByKey };
  }, [intl.locale, monthBase, previewPosts]);

  const handleGeneratePreview = useCallback(async () => {
    if (!canProceedToPreview) return;
    if (!companyId) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoCompany",
          defaultMessage: "No hay companyId disponible para NanoBanana.",
        })
      );
      return;
    }
    if (!logo?.file) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.generate.noLogo",
          defaultMessage: "Sube el logo para generar imágenes con IA.",
        })
      );
      return;
    }

    const filledUploads = images.filter(Boolean) as UploadedImage[];
    if (!filledUploads.length) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.generate.noImages",
          defaultMessage: "Sube imágenes de referencia para generar las publicaciones.",
        })
      );
      return;
    }

    const postsToGenerate = [...previewPosts];
    if (!postsToGenerate.length) return;

    if (!hasOpenAiConfigured) {
      toast.warning(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.notConfigured",
          defaultMessage:
            "Para generar textos con ChatGPT, configura la API key de OpenAI en Integraciones (Redes sociales).",
        })
      );
    }

    const industryLabel =
      INDUSTRIES.find((i) => i.value === industry)?.label ?? "Tu industria";
    const topicLabels = topics.map((t) => t.label);

    setIsGeneratingNanoPosts(true);
    setNanoPostsProgress({ done: 0, total: postsToGenerate.length });
    setPreviewSeed(Date.now());

    let generated = 0;
    let failed = 0;
    let textGenerated = 0;
    let textFailed = 0;

    try {
      for (let idx = 0; idx < postsToGenerate.length; idx += 1) {
        const post = postsToGenerate[idx];
        const themeLabel = topicLabels.length
          ? topicLabels[idx % topicLabels.length]
          : "General";
        const referenceUpload =
          images[post.contentImageIndex] ?? filledUploads[idx % filledUploads.length];

        if (!referenceUpload?.file) {
          failed += 1;
          setNanoPostsProgress({ done: idx + 1, total: postsToGenerate.length });
          continue;
        }

        try {
          const formData = new FormData();
          formData.append("prompt", buildNanoPostPrompt(industryLabel, themeLabel));
          formData.append("ecoMode", "false");
          formData.append("images", referenceUpload.file);
          formData.append("images", logo.file);
          formData.append("type", "IMAGETOIAMGE");
          formData.append("numImages", "1");
          formData.append("imageSize", getNanoBananaImageSizeForSocial("instagram"));

          const { data } = await socialApiClient().post(
            GENERATE_OR_EDIT_ENDPOINT,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              params: { companyId },
            }
          );

          const providerCode = data?.payload?.raw?.code;
          const providerMessage = (data?.payload?.raw?.msg || "").toString();
          const insufficientCredits =
            providerCode === 402 ||
            providerMessage.toLowerCase().includes("insufficient");

          if (insufficientCredits) {
            throw new Error(
              intl.formatMessage({
                id: "app.aiGeneration.errInsufficientCredits",
                defaultMessage:
                  "No tienes saldo suficiente en NanoBanana para generar la imagen. Recarga creditos e intentalo nuevamente.",
              })
            );
          }

          const taskId = data?.payload?.taskId || data?.data?.taskId || data?.taskId;
          if (!taskId) {
            throw new Error(
              intl.formatMessage({
                id: "app.socialAutomatedPosts.nanobanana.errNoTaskId",
                defaultMessage: "No se pudo iniciar la edición en NanoBanana.",
              })
            );
          }

          const resultImageUrl = await pollNanobananaTaskStatus(String(taskId));
          const { data: blob, headers } = await socialApiClient().get(
            DOWNLOAD_IMAGE_ENDPOINT,
            {
              responseType: "blob",
              params: { imageUrl: resultImageUrl, companyId },
            }
          );

          const contentType =
            headers["content-type"] || blob.type || "image/jpeg";
          const ext = contentType.includes("png")
            ? "png"
            : contentType.includes("webp")
              ? "webp"
              : "jpg";
          const file = new File(
            [blob],
            `nanobanana-post-${Date.now()}-${idx}.${ext}`,
            { type: contentType }
          );
          const previewUrl = URL.createObjectURL(file);

          setPostImageOverrides((prev) => {
            const prevItem = prev[post.id];
            if (prevItem?.previewUrl) URL.revokeObjectURL(prevItem.previewUrl);
            return {
              ...prev,
              [post.id]: { file, previewUrl },
            };
          });

          if (hasOpenAiConfigured) {
            try {
              const generatedText = await generatePostTextWithChatGpt(
                {
                  industryLabel,
                  themeLabel,
                  socialMedia: "Instagram",
                },
                companyId
              );
              if (generatedText) {
                setPostTextOverrides((prev) => ({
                  ...prev,
                  [post.id]: generatedText,
                }));
                textGenerated += 1;
              }
            } catch (e: any) {
              textFailed += 1;
              if (textFailed === 1) {
                toast.error(
                  e instanceof Error
                    ? e.message
                    : intl.formatMessage({
                        id: "app.socialAutomatedPosts.modal.editText.autoGenErr",
                        defaultMessage:
                          'No se pudo generar el texto. Prueba "Regenerar texto con ChatGPT".',
                      })
                );
              }
              // eslint-disable-next-line no-console
              console.error("Error generando texto del post con ChatGPT", e);
            }
          }
          generated += 1;
        } catch (e) {
          failed += 1;
          // eslint-disable-next-line no-console
          console.error("Error generando post con NanoBanana", e);
        } finally {
          setNanoPostsProgress({ done: idx + 1, total: postsToGenerate.length });
        }
      }

      if (generated > 0) {
        toast.success(
          intl.formatMessage(
            {
              id: "app.socialAutomatedPosts.generate.ok",
              defaultMessage: "Imágenes generadas con IA: {count}.",
            },
            { count: generated }
          )
        );
      }
      if (failed > 0) {
        toast.warn(
          intl.formatMessage(
            {
              id: "app.socialAutomatedPosts.generate.partial",
              defaultMessage:
                "Algunas publicaciones no se pudieron generar ({count} errores).",
            },
            { count: failed }
          )
        );
      }
      if (textGenerated > 0) {
        toast.success(
          intl.formatMessage(
            {
              id: "app.socialAutomatedPosts.generate.textOk",
              defaultMessage: "Textos generados con ChatGPT: {count}.",
            },
            { count: textGenerated }
          )
        );
      }
      if (textFailed > 0) {
        toast.warn(
          intl.formatMessage(
            {
              id: "app.socialAutomatedPosts.generate.textPartial",
              defaultMessage:
                "Algunos textos no se pudieron generar ({count} errores).",
            },
            { count: textFailed }
          )
        );
      }
    } finally {
      setIsGeneratingNanoPosts(false);
    }
  }, [
    buildNanoPostPrompt,
    canProceedToPreview,
    companyId,
    getNanoBananaImageSizeForSocial,
    generatePostTextWithChatGpt,
    images,
    industry,
    intl,
    logo,
    pollNanobananaTaskStatus,
    previewPosts,
    topics,
    hasOpenAiConfigured,
  ]);

  const applyPostTextDraft = useCallback(() => {
    if (!activePreviewPost) return;
    const value = postTextDraft.trim();
    if (!value) return;
    setPostTextOverrides((prev) => ({
      ...prev,
      [activePreviewPost.id]: value,
    }));
    setActivePreviewPost((prev) => (prev ? { ...prev, text: value } : prev));
    toast.success(
      intl.formatMessage({
        id: "app.socialAutomatedPosts.modal.editText.applied",
        defaultMessage: "Texto actualizado correctamente",
      })
    );
  }, [activePreviewPost, intl, postTextDraft]);

  const regenerateActivePostText = useCallback(async () => {
    if (!activePreviewPost || !companyId) return;
    if (!hasOpenAiConfigured) {
      toast.warning(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.notConfigured",
          defaultMessage:
            "Para generar textos con ChatGPT, configura la API key de OpenAI en Integraciones (Redes sociales).",
        })
      );
      return;
    }
    setIsGeneratingPostText(true);
    try {
      const postIndex = previewPosts.findIndex(
        (p) => p.id === activePreviewPost.id
      );
      const industryLabel =
        INDUSTRIES.find((i) => i.value === industry)?.label ?? "Tu industria";
      const themeLabel =
        topics.length > 0
          ? topics[(postIndex >= 0 ? postIndex : 0) % topics.length].label
          : "General";
      const socialMedia =
        OPENAI_SOCIAL_NAME[activeSocialPreview] ?? "Instagram";
      const generatedText = await generatePostTextWithChatGpt(
        {
          industryLabel,
          themeLabel,
          socialMedia,
        },
        companyId
      );
      setPostTextOverrides((prev) => ({
        ...prev,
        [activePreviewPost.id]: generatedText,
      }));
      setPostTextDraft(generatedText);
      setActivePreviewPost((prev) =>
        prev ? { ...prev, text: generatedText } : prev
      );
      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.editText.regenerated",
          defaultMessage: "Texto regenerado con ChatGPT",
        })
      );
    } catch (e: any) {
      toast.error(
        e?.message ||
          intl.formatMessage({
            id: "app.socialAutomatedPosts.modal.editText.regenerateErr",
            defaultMessage: "No se pudo regenerar el texto del post.",
          })
      );
    } finally {
      setIsGeneratingPostText(false);
    }
  }, [
    activePreviewPost,
    activeSocialPreview,
    companyId,
    generatePostTextWithChatGpt,
    industry,
    intl,
    previewPosts,
    topics,
    hasOpenAiConfigured,
  ]);

  const handleCreate = useCallback(async () => {
    if (!canProceedToPreview || !previewDates.length) return;

    if (!companyId) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoCompany",
          defaultMessage:
            "No hay companyId disponible para NanoBanana.",
        })
      );
      return;
    }

    const allApprovedLocal =
      previewPosts.length > 0 &&
      previewPosts.every((p) => Boolean(approvedPostIds[p.id]));

    if (!allApprovedLocal) {
      toast.error(
        "Para enviar al calendario, aprueba todas las publicaciones marcando la casilla en cada una."
      );
      return;
    }

    const syncedTargets = getAutoPostRequiredSocials().filter(
      (s) => !missingRequiredSocials.includes(s.previewId)
    );
    if (!syncedTargets.length) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.syncMissing",
          defaultMessage:
            "Para enviar al calendario, primero sincroniza las redes sociales requeridas.",
        })
      );
      setSyncAccountsModalOpen(true);
      return;
    }

    setIsGenerating(true);
    try {
      const filledUploads = images.filter(Boolean) as UploadedImage[];
      const imageByFileKey = new Map<string, Promise<PostImage>>();
      let sentCount = 0;
      let failedCount = 0;

      const getPostImageFromUpload = (upload: UploadedImage, idx: number) => {
        const file = upload.file;
        const key = `${file.name}_${file.lastModified}_${file.size}`;
        if (!imageByFileKey.has(key)) {
          imageByFileKey.set(
            key,
            (async () => {
              const data = (await getBase64(file)) as string;
              return {
                id: `${key}_${idx}`,
                lastModified: file.lastModified,
                name: file.name,
                size: file.size,
                type: file.type || "image/jpeg",
                data,
              };
            })()
          );
        }
        return imageByFileKey.get(key)!;
      };

      for (let idx = 0; idx < previewPosts.length; idx += 1) {
        const post = previewPosts[idx];
        const upload =
          postImageOverrides[post.id] ||
          images[post.contentImageIndex] ||
          filledUploads[idx % Math.max(filledUploads.length, 1)];

        if (!upload?.file) {
          failedCount += syncedTargets.length;
          continue;
        }

        const text = (postTextOverrides[post.id] ?? post.text ?? "").trim();
        const scheduleDate = buildScheduleDate(post.date, post.time);
        const postImage = await getPostImageFromUpload(upload, idx);

        for (const target of syncedTargets) {
          try {
            if (target.previewId === "instagram") {
              await instagramApiService.socialPost(
                text,
                [],
                [postImage],
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "facebook") {
              await facebookApiService.socialPost(
                text,
                [],
                [postImage],
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "linkedin") {
              await linkedInApiService.socialPost(
                text,
                [],
                [postImage],
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "twitter") {
              await twitterApiService.socialPost(
                text,
                [],
                [postImage],
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "tiktok") {
              await tiktokApiService.socialPost(
                text,
                [],
                [postImage],
                scheduleDate,
                "",
                companyId
              );
            }
            sentCount += 1;
          } catch (error) {
            failedCount += 1;
            // eslint-disable-next-line no-console
            console.error("Error sending automated post to calendar", {
              error,
              target: target.previewId,
              postId: post.id,
            });
          }
        }
      }

      if (sentCount > 0 && failedCount === 0) {
        toast.success(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.sendCalendar.ok",
            defaultMessage:
              "Publicaciones enviadas al calendario y programadas correctamente.",
          })
        );
        goToSocialCalendar();
      } else if (sentCount > 0) {
        toast.warn(
          intl.formatMessage(
            {
              id: "app.socialAutomatedPosts.sendCalendar.partial",
              defaultMessage:
                "Se programaron {ok} publicaciones, pero {fail} fallaron. Revisa las conexiones y vuelve a intentarlo.",
            },
            { ok: sentCount, fail: failedCount }
          )
        );
        goToSocialCalendar();
      } else {
        toast.error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.sendCalendar.error",
            defaultMessage:
              "No se pudo enviar al calendario. Revisa las redes sincronizadas e inténtalo de nuevo.",
          })
        );
      }

      setApprovalMode(false);
    } finally {
      setIsGenerating(false);
    }
  }, [
    approvedPostIds,
    canProceedToPreview,
    companyId,
    images,
    previewDates,
    previewPosts,
    intl,
    missingRequiredSocials,
    postImageOverrides,
    postTextOverrides,
    goToSocialCalendar,
  ]);

  const allApproved = useMemo(() => {
    return (
      previewPosts.length > 0 &&
      previewPosts.every((p) => Boolean(approvedPostIds[p.id]))
    );
  }, [approvedPostIds, previewPosts]);

  const canSendToCalendar = hasAnySocialSynced && allApproved;

  const approvedCount = useMemo(() => {
    return previewPosts.reduce(
      (acc, p) => acc + (approvedPostIds[p.id] ? 1 : 0),
      0
    );
  }, [approvedPostIds, previewPosts]);

  // Vista listado de proyectos (antes de empezar el flujo).
  if (!currentProjectId) {
    return (
      <Layout>
        <Header2
          title={intl.formatMessage({
            id: "app.sidebar.socialMediaAutomatedPosts",
          })}
        />
        <Content className="p-0">
          <div className="social-auto">
            <div className="social-auto__top">
              <div className="social-auto__previewIntroTitle">
                {intl.formatMessage({
                  id: "app.socialAutomatedPosts.projects.title",
                  defaultMessage: "Proyectos de publicaciones IA",
                })}
              </div>
              <div className="social-auto__subtitle">
                {intl.formatMessage({
                  id: "app.socialAutomatedPosts.projects.subtitle",
                  defaultMessage:
                    "Cada proyecto es una serie de publicaciones. Crea uno para empezar o abre uno existente para seguir editando.",
                })}
              </div>
            </div>
            <div className="social-auto__body">
              <div className="social-auto__panel social-auto__panel--right">
                <div className="social-auto__projectCreate">
                  <Input
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "app.socialAutomatedPosts.projects.namePlaceholder",
                      defaultMessage: "Nombre del proyecto",
                    })}
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        createProject(newProjectName);
                        setNewProjectName("");
                      }
                    }}
                    className="social-auto__projectNameInput"
                  />
                  <Button
                    color="primary"
                    onClick={() => {
                      createProject(newProjectName);
                      setNewProjectName("");
                    }}
                  >
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.projects.create",
                      defaultMessage: "Crear proyecto",
                    })}
                  </Button>
                </div>
                <div className="social-auto__projectList">
                  {projects.length === 0 ? (
                    <div className="social-auto__projectListEmpty text-muted">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.projects.empty",
                        defaultMessage: "Aún no hay proyectos. Crea uno arriba.",
                      })}
                    </div>
                  ) : (
                    projects.map((p) => (
                      <div
                        key={p.id}
                        className="social-auto__projectCard"
                        role="button"
                        tabIndex={0}
                        onClick={() => openProject(p)}
                      >
                        <div className="social-auto__projectCardName">{p.name}</div>
                        <div className="social-auto__projectCardDate text-muted small">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.projects.createdOn",
                            defaultMessage: "Creado el",
                          })}{" "}
                          {new Date(p.createdAt).toLocaleDateString(intl.locale || "es", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProject(p);
                          }}
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.projects.open",
                            defaultMessage: "Abrir",
                          })}
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteProject(p.id, p.name);
                          }}
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.projects.delete",
                            defaultMessage: "Eliminar",
                          })}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({
          id: "app.sidebar.socialMediaAutomatedPosts",
        })}
      />
      <Content className="p-0">
        <div className="social-auto">
          <div className="social-auto__top">
            <div className="social-auto__stepper">
              <div className="social-auto__stepLabel social-auto__stepLabel--project">
                {currentProjectName}
              </div>
              {([
                { id: "config", label: "1. Config" },
                { id: "media", label: "2. Medios" },
                { id: "preview", label: "3. Vista previa" },
              ] as Array<{ id: StepId; label: string }>).map((s, i) => {
                const idx = i;
                const isActive = step === s.id;
                const isDone =
                  (step === "media" && idx <= 0) ||
                  (step === "preview" && idx <= 1);
                return (
                  <div
                    key={s.id}
                    className={cn("social-auto__step", {
                      "social-auto__step--active": isActive,
                      "social-auto__step--done": isDone,
                    })}
                  >
                    <div className="social-auto__stepCircle">{idx + 1}</div>
                    <div className="social-auto__stepLabel">{s.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="social-auto__subtitle">
              {intl.formatMessage({
                id: "app.socialAutomatedPosts.description",
                defaultMessage:
                  "Configura tu automatización: textos + imágenes generadas con IA. (Front mock)",
              })}
            </div>
          </div>

          <div className="social-auto__body">
            <div className="social-auto__panel social-auto__panel--right">
              {step === "config" && (
                <div className="social-auto__stepContent social-auto__stepContent--config">
                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.industry",
                        defaultMessage: "1) Industria",
                      })}
                    </Label>
                    <Input
                      type="select"
                      value={industry}
                      onChange={(e) => handleIndustryChange(e.target.value)}
                      className={cn("social-auto__input", {
                        "is-invalid": errors.industry,
                      })}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: "app.select.placeholder",
                          defaultMessage: "Seleccionar...",
                        })}
                      </option>
                      {INDUSTRIES.map((opt) => (
                        <option value={opt.value} key={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    {errors.industry && (
                      <div className="invalid-feedback">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.error.industry",
                          defaultMessage: "Requerido.",
                        })}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.topics",
                        defaultMessage: "2) Temáticas (selecciona varias)",
                      })}
                    </Label>
                    <Creatable
                      isMulti
                      options={TOPIC_TAGS}
                      value={topics}
                      className={cn("social-auto__reactSelect social-auto__reactSelect--modern", {
                        "social-auto__reactSelect--invalid": errors.topics,
                      })}
                      classNamePrefix="select"
                      placeholder={intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.topicsPlaceholder",
                        defaultMessage: "Busca, selecciona o escribe y pulsa Intro para agregar una temática personalizada",
                      })}
                      formatCreateLabel={(inputValue) =>
                        intl.formatMessage(
                          {
                            id: "app.socialAutomatedPosts.form.topicsCreateLabel",
                            defaultMessage: 'Agregar "{inputValue}"',
                          },
                          { inputValue }
                        )
                      }
                      onChange={(value: any) =>
                        setTopics((value as Option[]) ?? [])
                      }
                    />
                    {errors.topics && (
                      <div className="text-danger small mt-1">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.error.topicsReq",
                          defaultMessage: "Elige al menos 1 temática.",
                        })}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.days",
                        defaultMessage: "3) Días (máx. 2)",
                      })}
                    </Label>
                    <div className="social-auto__days">
                      {DAYS.map((d) => {
                        const active = selectedDays.includes(d.key);
                        return (
                          <button
                            key={d.key}
                            type="button"
                            className={cn(
                              "social-auto__dayBtn",
                              active && "social-auto__dayBtn--active"
                            )}
                            onClick={() => toggleDay(d.key)}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.days && (
                      <div className="text-danger small mt-1">
                        {selectedDays.length > 2
                          ? intl.formatMessage({
                              id: "app.socialAutomatedPosts.error.daysMax",
                              defaultMessage:
                                "Elige máximo 2 días por semana.",
                            })
                          : intl.formatMessage({
                              id: "app.socialAutomatedPosts.error.daysReq",
                              defaultMessage:
                                "Selecciona al menos 1 día.",
                            })}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.startDate",
                        defaultMessage: "4) A partir de qué fecha",
                      })}
                    </Label>
                    <DatePicker
                      className={cn("w-100 social-auto__datePicker", {
                        "is-invalid": errors.startDate,
                      })}
                      calendarClassName="social-auto__calendar"
                      minDate={new Date()}
                      value={startDate}
                      onChange={(d: any) => setStartDate(toDate(d))}
                    />
                    <div className="text-muted small mt-1">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.startDateHint",
                        defaultMessage: "Máximo 8 publicaciones al mes.",
                      })}
                    </div>
                    {errors.startDate && (
                      <div className="text-danger small mt-1">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.error.startDateReq",
                          defaultMessage: "Selecciona una fecha.",
                        })}
                      </div>
                    )}
                  </FormGroup>

                  <div className="social-auto__smartSchedule">
                    <button
                      type="button"
                      className="social-auto__smartScheduleHeader"
                      onClick={() => setSmartScheduleOpen((o) => !o)}
                      aria-expanded={smartScheduleOpen}
                    >
                      <span className="social-auto__smartScheduleTitle">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.smartSchedule.title",
                          defaultMessage: "Utilizar un horario inteligente",
                        })}
                      </span>
                      <span
                        className={cn("social-auto__smartScheduleChevron", {
                          "social-auto__smartScheduleChevron--open": smartScheduleOpen,
                        })}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {smartScheduleOpen && (
                      <div className="social-auto__smartScheduleBody">
                        <div className="social-auto__smartScheduleAlert">
                          <span className="social-auto__smartScheduleAlertIcon" aria-hidden>i</span>
                          <span>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.smartSchedule.hint",
                              defaultMessage:
                                "Los horarios recomendados se basan en el análisis de engagement de tu audiencia en las últimas 4 semanas.",
                            })}
                          </span>
                        </div>
                        <div className="social-auto__smartScheduleTableWrap">
                          <table className="social-auto__smartScheduleTable">
                            <thead>
                              <tr>
                                <th>
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.smartSchedule.colTime",
                                    defaultMessage: "Horarios recomendados",
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.smartSchedule.colEngagement",
                                    defaultMessage: "Engagement",
                                  })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {smartScheduleSuggestions.map((row, idx) => (
                                <tr
                                  key={row.time + row.label}
                                  role="button"
                                  tabIndex={0}
                                  className={cn(
                                    "social-auto__smartScheduleRow",
                                    idx === 0 && "social-auto__smartScheduleRow--top",
                                    selectedSmartTime === row.time && "social-auto__smartScheduleRow--selected"
                                  )}
                                  onClick={() => {
                                    setSelectedSmartTime(row.time);
                                    toast.success(
                                      intl.formatMessage(
                                        {
                                          id: "app.socialAutomatedPosts.smartSchedule.applied",
                                          defaultMessage: "Horario aplicado: {time}. Se verá en el calendario.",
                                        },
                                        { time: row.label }
                                      )
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setSelectedSmartTime(row.time);
                                      toast.success(
                                        intl.formatMessage(
                                          {
                                            id: "app.socialAutomatedPosts.smartSchedule.applied",
                                            defaultMessage: "Horario aplicado: {time}. Se verá en el calendario.",
                                          },
                                          { time: row.label }
                                        )
                                      );
                                    }
                                  }}
                                >
                                  <td>{row.label}</td>
                                  <td>
                                    <span className="social-auto__smartSchedulePct">
                                      {row.engagement}
                                    </span>
                                    <span className="social-auto__smartScheduleStars">
                                      {"★".repeat(row.stars)}
                                      {"☆".repeat(5 - row.stars)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="social-auto__smartScheduleFooter">
                          <Button
                            color="primary"
                            outline
                            size="sm"
                            onClick={() => toast.info(intl.formatMessage({
                              id: "app.socialAutomatedPosts.smartSchedule.seeAllHint",
                              defaultMessage: "Próximamente: ver todas las horas",
                            }))}
                          >
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.smartSchedule.seeAll",
                              defaultMessage: "Ver todas las horas",
                            })}
                            {" "}
                            <span aria-hidden>▼</span>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="social-auto__actions social-auto__actions--spaceBetween social-auto__actions--config">
                    <Button color="link" className="social-auto__backBtn" onClick={backToProjectList}>
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.backToProjects",
                        defaultMessage: "Volver a proyectos",
                      })}
                    </Button>
                    <Button color="primary" className="social-auto__primaryBtn" onClick={validateAndNext}>
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.nextToMedia",
                        defaultMessage: "Continuar: Medios",
                      })}
                    </Button>
                  </div>
                </div>
              )}

              {step === "media" && (
                <div className="social-auto__stepContent">
                  <div className="social-auto__mediaHeader">
                    <div className="social-auto__mediaTitle">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.mediaTitle",
                        defaultMessage: "Sube tus recursos para crear contenido",
                      })}
                    </div>
                    <div className="text-muted small">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.mediaHint",
                        defaultMessage:
                          "El logo y las 3 imágenes se usarán como base para la generación (mock).",
                      })}
                    </div>
                  </div>

                  <div className="social-auto__uploadGrid">
                    <div className="social-auto__uploadBox">
                      <div className="social-auto__uploadLabel">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.logo",
                          defaultMessage: "5) Logo de la empresa",
                        })}
                      </div>
                      <div
                        className={cn("social-auto__drop", {
                          "social-auto__drop--invalid": Boolean(errors.logo),
                        })}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const el = document.getElementById(
                            "logo-input"
                          ) as HTMLInputElement | null;
                          el?.click();
                        }}
                      >
                        {logo ? (
                          <img
                            src={logo.previewUrl}
                            alt="logo"
                            className="social-auto__dropImg"
                          />
                        ) : (
                          <div className="social-auto__dropPlaceholder">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.upload.logo",
                              defaultMessage: "Click para subir logo",
                            })}
                          </div>
                        )}
                      </div>

                      <input
                        id="logo-input"
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) =>
                          onLogoSelected(e.target.files?.[0] ?? null)
                        }
                      />
                      {errors.logo && (
                        <div className="text-danger small mt-1">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.error.logoReq",
                            defaultMessage: "Logo requerido.",
                          })}
                        </div>
                      )}
                    </div>

                    <div className="social-auto__uploadBox">
                      <div className="social-auto__uploadLabel">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.images",
                          defaultMessage: "6) hasta 5 imágenes para contenido",
                        })}
                      </div>

                      <div
                        className={cn(
                          "social-auto__imagesDropZone",
                          isDraggingImages &&
                            "social-auto__imagesDropZone--drag"
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setReplaceSlotIndex(null);
                          const el = document.getElementById(
                            "images-input"
                          ) as HTMLInputElement | null;
                          el?.click();
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(false);
                          const files = e.dataTransfer.files;
                          pickImagesForSlots(files, replaceSlotIndex);
                          setReplaceSlotIndex(null);
                        }}
                      >
                        <div className="social-auto__imagesDropZoneTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.drop.title",
                            defaultMessage:
                              "Arrastra o Suelta imagenes aquí",
                          })}
                        </div>
                        <div className="social-auto__imagesDropZoneHint text-muted small">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.drop.hint",
                            defaultMessage:
                              "O clic para seleccionar (máx. 5). Mín. 3 para previsualizar.",
                          })}
                        </div>
                      </div>

                      <div className="social-auto__imagesGrid">
                        {images.map((img, idx) => (
                          <div
                            key={idx}
                            className={cn("social-auto__imgSlot", {
                              "social-auto__imgSlot--filled": Boolean(img),
                              "social-auto__imgSlot--invalid": Boolean(
                                errors.images
                              ),
                            })}
                          >
                            <div className="social-auto__imgSlotHeader">
                              <span>#{idx + 1}</span>
                              {img && (
                                <button
                                  type="button"
                                  className="social-auto__imgSlotRemove"
                                  onClick={() => clearSlot(idx)}
                                >
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.remove",
                                    defaultMessage: "Quitar",
                                  })}
                                </button>
                              )}
                            </div>

                            <div
                              className="social-auto__imgSlotDrop"
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setReplaceSlotIndex(idx);
                                const el = document.getElementById(
                                  "images-input"
                                ) as HTMLInputElement | null;
                                el?.click();
                              }}
                            >
                              {img ? (
                                <img
                                  src={img.previewUrl}
                                  alt={`img ${idx + 1}`}
                                  className="social-auto__imgSlotImg"
                                />
                              ) : (
                                <div className="social-auto__imgSlotPlaceholder">
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.upload.image",
                                    defaultMessage: "Elegir",
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <input
                        id="images-input"
                        type="file"
                        accept="image/*"
                        className="d-none"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          pickImagesForSlots(files, replaceSlotIndex);
                          setReplaceSlotIndex(null);
                          // reset input so selecting same file again works
                          e.currentTarget.value = "";
                        }}
                      />

                      {errors.images && (
                        <div className="text-danger small mt-1">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.error.imagesReq",
                            defaultMessage:
                              "Sube entre 3 y 5 imágenes.",
                          })}
                          {errors.images === "requiredMin"
                            ? ` (mínimo ${MIN_CONTENT_IMAGES})`
                            : errors.images === "requiredMax"
                              ? ` (máximo ${MAX_CONTENT_IMAGES})`
                              : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="social-auto__actions social-auto__actions--spaceBetween">
                    <Button
                      color="link"
                      onClick={() => setStep("config")}
                    >
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.back",
                        defaultMessage: "Volver",
                      })}
                    </Button>
                    <Button color="primary" onClick={validateAndNextToPreview}>
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.nextToPreview",
                        defaultMessage: "Ver: Vista previa",
                      })}
                    </Button>
                  </div>
                </div>
              )}

              {step === "preview" && (
                <div className="social-auto__stepContent">
                  <div className="social-auto__previewIntro">
                    <div className="social-auto__previewIntroTitle">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.preview.title",
                        defaultMessage: "Previsualización de publicaciones",
                      })}
                    </div>
                    <div className="social-auto__previewIntroActions">
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={handleGeneratePreview}
                        disabled={isGeneratingNanoPosts || isGenerating}
                      >
                        {isGeneratingNanoPosts
                          ? intl.formatMessage(
                              {
                                id: "app.socialAutomatedPosts.preview.generatingAI",
                                defaultMessage:
                                  "Generando imágenes IA ({done}/{total})...",
                              },
                              {
                                done: nanoPostsProgress.done,
                                total: nanoPostsProgress.total,
                              }
                            )
                          : intl.formatMessage({
                              id: "app.socialAutomatedPosts.preview.regenerate",
                              defaultMessage: "Generar imágenes con IA",
                            })}
                      </Button>
                    </div>
                  </div>

                  <div className="social-auto__calendarWrap">
                    <div className="social-auto__calendarHeader">
                      <div className="social-auto__calendarTitle">
                        {calendarModel.title}
                      </div>
                      <div className="social-auto__calendarHint text-muted small">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.calendar.hint",
                          defaultMessage:
                            "Mostrando imágenes del post por día (IA + referencias).",
                        })}
                      </div>

                      {approvalMode ? (
                        <div className="social-auto__approvalMasterInCalendar">
                          <input
                            type="checkbox"
                            checked={allApproved}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (!checked) {
                                setApprovedPostIds({});
                                return;
                              }
                              const next: Record<string, boolean> = {};
                              previewPosts.forEach((p) => {
                                next[p.id] = true;
                              });
                              setApprovedPostIds(next);
                            }}
                          />
                          <span>Check todo</span>
                          <span className="text-muted small ml-2">
                            {approvedCount}/{previewPosts.length} aprobados
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <div className="social-auto__calendarGrid">
                      {calendarModel.weekDays.map((d) => (
                        <div key={d} className="social-auto__calendarDow">
                          {d}
                        </div>
                      ))}

                      {calendarModel.cells.map((cell, idx) => {
                        const post = cell.key ? calendarModel.daysByKey.get(cell.key) : undefined;
                        const hasPost = Boolean(post);
                        return (
                          <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${cell.key ?? "empty"}-${idx}`}
                            className={cn("social-auto__calendarCell", {
                              "social-auto__calendarCell--empty": !cell.key,
                              "social-auto__calendarCell--hasPost": hasPost,
                            })}
                            role={post ? "button" : undefined}
                            tabIndex={post ? 0 : undefined}
                            onClick={() => {
                              if (!post) return;
                              if (approvalMode) {
                                setApprovedPostIds((prev) => ({
                                  ...prev,
                                  [post.id]: !prev[post.id],
                                }));
                                return;
                              }
                              setActivePreviewPost(post);
                              setActiveSocialPreview(
                                syncedSocialPreviews[0]?.id ?? "instagram"
                              );
                              setNanoEditPrompt("");
                              setNanoEditProgress(0);
                              setIsEditingNanoBanana(false);
                              setIsPostModalOpen(true);
                            }}
                          >
                            {cell.dayNumber != null && (
                              <div className="social-auto__calendarDayNumber">
                                {cell.dayNumber}
                              </div>
                            )}

                            {post?.image ? (
                              <img
                                src={post.image}
                                alt="scheduled"
                                className="social-auto__calendarCellImg"
                              />
                            ) : null}

                            {post ? (
                              <div className="social-auto__calendarCellOverlay">
                                <div className="social-auto__calendarCellOverlayTime">
                                  {post.time}
                                </div>
                                <div className="social-auto__calendarCellOverlayAI">
                                  AI {Math.round(post.confidence * 100)}%
                                </div>
                              </div>
                            ) : null}

                            {approvalMode && post ? (
                              <div
                                className="social-auto__calendarCellApproval"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={Boolean(approvedPostIds[post.id])}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setApprovedPostIds((prev) => ({
                                      ...prev,
                                      [post.id]: checked,
                                    }));
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="social-auto__actions social-auto__actions--spaceBetween">
                    <Button
                      color="link"
                      onClick={() => setStep("media")}
                      disabled={isGenerating}
                    >
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.backToMedia",
                        defaultMessage: "Editar medios",
                      })}
                    </Button>

                    {!approvalMode ? (
                      <Button
                        color="primary"
                        onClick={() => {
                          // Entrar a modo aprobación (UI delicada).
                          setApprovalMode(true);
                          setApprovedPostIds({});
                        }}
                        disabled={isGenerating}
                      >
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.toApproval",
                          defaultMessage:
                            "Aprobar publicaciones (checkbox todo)",
                        })}
                      </Button>
                    ) : (
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: 12 }}
                      >
                        {missingRequiredSocials.length > 0 ? (
                          <Button
                            color="warning"
                            onClick={() => setSyncAccountsModalOpen(true)}
                            disabled={isGenerating}
                          >
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.syncNow",
                              defaultMessage: "Sincronizar redes",
                            })}
                          </Button>
                        ) : null}

                        <Button
                          color="primary"
                          onClick={handleCreate}
                          disabled={isGenerating || !canSendToCalendar}
                        >
                          {isGenerating
                            ? intl.formatMessage({
                                id: "app.socialAutomatedPosts.creating",
                                defaultMessage: "Enviando...",
                              })
                            : intl.formatMessage({
                                id: "app.socialAutomatedPosts.sendCalendar",
                                defaultMessage: "Enviar al calendario",
                              })}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="social-auto__jsonWrap">
                    <div className="social-auto__jsonTitle">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.payloadTitle",
                        defaultMessage: "Payload para backend (mock)",
                      })}
                    </div>
                    <ReactJson
                      name="payload"
                      displayDataTypes={false}
                      src={{
                        ...config,
                        schedule: previewDates.map((d) => ({
                          date: formatDate(d),
                          day: d.getDay(),
                        })),
                      }}
                    />
                  </div>
                </div>
              )}

              <Modal isOpen={isPostModalOpen} toggle={closePostModal} size="lg">
                <ModalHeader toggle={closePostModal}>
                  {activePreviewPost
                    ? `${formatDate(activePreviewPost.date)} · ${activePreviewPost.time}`
                    : "Post"}
                </ModalHeader>
                <ModalBody>
                  {activePreviewPost ? (
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="font-weight-bold">
                          {activePreviewPost.title}
                        </div>
                        <div className="text-primary font-weight-bold">
                          AI: {Math.round(activePreviewPost.confidence * 100)}%
                        </div>
                      </div>

                      <div className="social-auto__modalSocialTabs">
                        {syncedSocialPreviews.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className={cn("social-auto__modalSocialTab", {
                              "social-auto__modalSocialTab--active":
                                s.id === activeSocialPreview,
                            })}
                            onClick={() => setActiveSocialPreview(s.id)}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>

                      <div
                        className={cn(
                          "social-auto__modalPreviewFrame",
                          `social-auto__modalPreviewFrame--${activeSocialPreview}`
                        )}
                      >
                        {activePreviewPost.image ? (
                          <img
                            src={activePreviewPost.image}
                            alt="preview"
                            className="social-auto__modalPreviewImg"
                          />
                        ) : (
                          <div className="social-auto__modalPreviewEmpty" />
                        )}

                        <div className="social-auto__modalPreviewBadge">
                          {syncedSocialPreviews.find(
                            (s) => s.id === activeSocialPreview
                          )?.label ?? SOCIAL_PREVIEWS.find((s) => s.id === activeSocialPreview)?.label ?? ""}
                        </div>
                      </div>

                      <input
                        ref={modalImageInputRef}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        aria-hidden
                        onChange={handleChangePostImageByFile}
                      />
                      <div className="d-flex justify-content-center mb-3">
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          onClick={() => modalImageInputRef.current?.click()}
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.changeImage.btn",
                            defaultMessage: "Subir otra imagen",
                          })}
                        </Button>
                      </div>

                      {activePreviewPost ? (
                        <div className="social-auto__modalSchedule mb-3">
                          <div className="social-auto__modalScheduleTitle">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.schedule.title",
                              defaultMessage: "Horario recomendado de la publicación (IA)",
                            })}
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <DatePicker
                              className="social-auto__modalScheduleDate"
                              calendarClassName="social-auto__calendar"
                              value={activePreviewPost.date}
                              onChange={(value: Date | null) => {
                                if (!activePreviewPost || !value) return;
                                const time = activePreviewPost.time;
                                const next = { date: value, time };
                                setPostScheduleOverrides((prev) => ({
                                  ...prev,
                                  [activePreviewPost.id]: next,
                                }));
                                const caption = resolveCaptionAfterScheduleChange(
                                  activePreviewPost.id,
                                  value,
                                  time
                                );
                                setActivePreviewPost((prev) =>
                                  prev
                                    ? { ...prev, date: value, text: caption }
                                    : prev
                                );
                              }}
                              format="dd/MM/yyyy"
                              dayPlaceholder="dd"
                              monthPlaceholder="mm"
                              yearPlaceholder="aaaa"
                            />
                            <input
                              type="time"
                              className="form-control social-auto__modalScheduleTime"
                              value={activePreviewPost.time}
                              onChange={(e) => {
                                const time = e.target.value;
                                if (!activePreviewPost) return;
                                const next = {
                                  date: activePreviewPost.date,
                                  time,
                                };
                                setPostScheduleOverrides((prev) => ({
                                  ...prev,
                                  [activePreviewPost.id]: next,
                                }));
                                const caption = resolveCaptionAfterScheduleChange(
                                  activePreviewPost.id,
                                  activePreviewPost.date,
                                  time
                                );
                                setActivePreviewPost((prev) =>
                                  prev ? { ...prev, time, text: caption } : prev
                                );
                              }}
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="social-auto__igPreview mb-3">
                        <div className="social-auto__igPreviewHeader">
                          {logo?.previewUrl ? (
                            <img
                              src={logo.previewUrl}
                              alt="logo"
                              className="social-auto__igPreviewAvatar"
                            />
                          ) : (
                            <div className="social-auto__igPreviewAvatar social-auto__igPreviewAvatar--placeholder">
                              W
                            </div>
                          )}
                          <div className="social-auto__igPreviewUser">
                            {currentProjectName || "Wembii"}
                          </div>
                        </div>
                        <div className="social-auto__igPreviewCaption">
                          {activePreviewPost.text}
                        </div>
                      </div>

                      {!hasOpenAiConfigured &&
                      isPlaceholderCaption(activePreviewPost.text) ? (
                        <div
                          className="alert alert-warning small py-2 mb-3"
                          role="status"
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.openai.notConfigured",
                            defaultMessage:
                              "Para usar ChatGPT, configura la API key de OpenAI en Redes sociales → Integraciones (tarjeta OpenAI).",
                          })}
                        </div>
                      ) : null}

                      <div className="social-auto__modalEditor">
                        <div className="social-auto__modalEditorTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editImage.title",
                            defaultMessage: "Editar imagen",
                          })}
                        </div>
                        <div className="social-auto__modalEditorHint">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editImage.hint",
                            defaultMessage:
                              "Cuéntanos qué quieres mejorar (estilo, fondo, colores, etc.). La IA devolverá una imagen lista para tu red social.",
                          })}
                        </div>

                        <textarea
                          className="form-control social-auto__modalEditorTextarea"
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editNano.placeholder",
                            defaultMessage:
                              "Ej: más profesional, fondo limpio, colores más vivos y tipografía legible.",
                          })}
                          value={nanoEditPrompt}
                          onChange={(e) => setNanoEditPrompt(e.target.value)}
                          disabled={isEditingNanoBanana}
                        />

                        <div className="social-auto__modalEditorActions">
                          <div
                            className="social-auto__modalEditorProgress text-muted small"
                            aria-live="polite"
                          >
                            {isEditingNanoBanana
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editNano.loading",
                                  defaultMessage: `Editando... ${nanoEditProgress}%`,
                                })
                              : null}
                          </div>

                          <Button
                            color="primary"
                            onClick={handleEditActivePostImage}
                            disabled={
                              isEditingNanoBanana ||
                              !nanoEditPrompt.trim() ||
                              !activePreviewPost
                            }
                          >
                            {isEditingNanoBanana
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editNano.btnLoading",
                                  defaultMessage: "Mejorando...",
                                })
                              : intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editNano.btn",
                                  defaultMessage: "Editar y ajustar imagen",
                                })}
                          </Button>
                        </div>
                      </div>

                      <div className="social-auto__modalEditor mt-3">
                        <div className="social-auto__modalEditorTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.title",
                            defaultMessage: "Modificar texto",
                          })}
                        </div>
                        <div className="social-auto__modalEditorHint">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.hint",
                            defaultMessage:
                              "Ajusta manualmente el texto o vuelve a generarlo con ChatGPT para esta publicación.",
                          })}
                        </div>
                        {isGeneratingPostText ? (
                          <div className="text-muted small mb-2">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.editText.regenerating",
                              defaultMessage: "Regenerando...",
                            })}
                          </div>
                        ) : null}
                        <textarea
                          className="form-control social-auto__modalEditorTextarea"
                          value={postTextDraft}
                          onChange={(e) => setPostTextDraft(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.placeholder",
                            defaultMessage:
                              "Escribe el texto de la publicación aquí...",
                          })}
                          disabled={isGeneratingPostText}
                        />
                        <div className="social-auto__modalEditorActions">
                          <Button
                            color="outline-secondary"
                            onClick={regenerateActivePostText}
                            disabled={isGeneratingPostText || !activePreviewPost}
                          >
                            {isGeneratingPostText
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editText.regenerating",
                                  defaultMessage: "Regenerando...",
                                })
                              : intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editText.regenerateBtn",
                                  defaultMessage: "Regenerar texto con ChatGPT",
                                })}
                          </Button>
                          <Button
                            color="primary"
                            onClick={applyPostTextDraft}
                            disabled={!postTextDraft.trim() || isGeneratingPostText}
                          >
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.editText.applyBtn",
                              defaultMessage: "Guardar texto",
                            })}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={closePostModal}>
                    Cerrar
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={isGeneratingNanoPosts}
                centered
                backdrop="static"
                keyboard={false}
              >
                <ModalBody>
                  <div className="social-auto__loadingModal">
                    <Spinner color="primary" />
                    <div className="social-auto__loadingModalTitle">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.generate.loadingTitle",
                        defaultMessage: "Generando imágenes y copy con IA",
                      })}
                    </div>
                    <div className="social-auto__loadingModalText">
                      {intl.formatMessage(
                        {
                          id: "app.socialAutomatedPosts.generate.loadingText",
                          defaultMessage:
                            "Estamos creando la imagen y el texto de cada publicación. Esto puede tardar unos segundos.",
                        }
                      )}
                    </div>
                    <div className="social-auto__loadingModalProgress">
                      {nanoPostsProgress.done}/{nanoPostsProgress.total}
                    </div>
                  </div>
                </ModalBody>
              </Modal>

              <Modal
                isOpen={syncAccountsModalOpen}
                toggle={() => setSyncAccountsModalOpen(false)}
                size="lg"
              >
                <ModalHeader toggle={() => setSyncAccountsModalOpen(false)}>
                  {intl.formatMessage({
                    id: "app.socialAutomatedPosts.syncModalTitle",
                    defaultMessage: "Sincronizar redes sociales",
                  })}
                </ModalHeader>
                <ModalBody>
                  <div className="mb-3">
                    {!hasAnySocialSynced ? (
                      intl.formatMessage({
                        id: "app.socialAutomatedPosts.syncModalNoneSynced",
                        defaultMessage:
                          "Ninguna red está sincronizada. Conecta al menos una a continuación para poder programar publicaciones.",
                      })
                    ) : (
                      intl.formatMessage({
                        id: "app.socialAutomatedPosts.syncModalHint",
                        defaultMessage:
                          "Conecta las redes que faltan para poder programar las publicaciones:",
                      })
                    )}
                  </div>

                  <div>
                    {getAutoPostRequiredSocials().filter((s) =>
                      missingRequiredSocials.includes(s.previewId)
                    ).map((s) => (
                      <div key={s.previewId} className="mb-3">
                        <SyncAccount
                          network={s.providerId}
                          existingAccounts={[] as any}
                          onSync={refreshSocialSettings}
                          onDisconnect={refreshSocialSettings}
                        />
                      </div>
                    ))}
                  </div>
                  {missingRequiredSocials.length === 0 && (
                    <div className="text-success font-weight-bold mt-2">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.syncModalAllDone",
                        defaultMessage: "Todas las redes están sincronizadas. Puedes cerrar y enviar al calendario.",
                      })}
                    </div>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="secondary"
                    onClick={() => setSyncAccountsModalOpen(false)}
                  >
                    {intl.formatMessage({
                      id: "app.close",
                      defaultMessage: "Cerrar",
                    })}
                  </Button>
                </ModalFooter>
              </Modal>
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  );
}

